import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    getUserExamTime, saveUserExamTime,
} from "../firebase-listeners/firestore-user";
import {getExamTime} from "../firebase-listeners/exams";

function incrementTime(examTime, increment, unit) {
    switch (unit) {
        case 'ms':
            examTime.milliseconds = (examTime.milliseconds || 0) + increment;
            while (examTime.milliseconds >= 1000) {
                examTime.milliseconds = examTime.milliseconds - 1000;
                examTime = incrementTime(examTime, 1, 's');
            }
            return examTime;
        case 's':
            examTime.seconds = (examTime.seconds || 0) + 1;
            while (examTime.seconds >= 60) {
                examTime.seconds = examTime.seconds - 60;
                examTime = incrementTime(examTime, 1, 'm');
            }
            return examTime;
        case 'm':
            examTime.minutes = (examTime.minutes || 0) + 1;
            while (examTime.minutes >= 60) {
                examTime.minutes = examTime.minutes - 60;
                examTime = incrementTime(examTime, 1, 'h');
            }
            return examTime;
        case 'h':
            examTime.hours = (examTime.hours || 0) + increment;
            return examTime;
        default:
            return examTime;
    }
}

function decrementTime(examTime, increment, unit) {
    switch (unit) {
        case 'ms':
            examTime.milliseconds = (examTime.milliseconds || 0) - increment;
            while (examTime.milliseconds < 0) {
                examTime.milliseconds += 999;
                examTime = decrementTime(examTime, 1, 's');
            }
            return examTime;
        case 's':
            examTime.seconds = (examTime.seconds || 0) - increment;
            while (examTime.seconds < 0) {
                examTime.seconds += 59;
                examTime = decrementTime(examTime, 1, 'm');
            }
            return examTime;
        case 'm':
            examTime.minutes = (examTime.minutes || 0) - increment;
            while (examTime.minutes < 0) {
                examTime.minutes += 59;
                examTime = decrementTime(examTime, 1, 'h');
            }
            return examTime;
        case 'h':
            examTime.hours = (examTime.hours || 0) - increment;
            return examTime;
        default:
            return examTime;
    }
}

export default function Timer({background = 'false', exam, examKey, uid}) {
    const userExamTimeKey = `EXAM-TIME-${uid}-${examKey}`;

    const [paused, setPaused] = React.useState(false);
    const [over, setOver] = React.useState(false);
    const [userExamTime, setUserExamTime] = useState({
        hours: 0,
        minutes: 0,
        seconds: 0,
        loaded: false,
    });

    const [examMaxTime, setExamMaxTime] = useState({
        hours: 0,
        minutes: 0,
        seconds: 0,
        loaded: false,
    });

    const saveTracker = useRef(0);

    const [loaded, setLoaded] = useState({max: false, user: false});

    const time = useMemo(() => {
        let timeLeft = {
            hours: examMaxTime.hours,
            minutes: examMaxTime.minutes,
            seconds: examMaxTime.seconds,
            milliseconds: examMaxTime.milliseconds
        };
        timeLeft = decrementTime(timeLeft, userExamTime?.hours || 0, 'h');
        timeLeft = decrementTime(timeLeft, userExamTime?.minutes || 0, 'm');
        timeLeft = decrementTime(timeLeft, userExamTime?.seconds || 0, 's');
        timeLeft = decrementTime(timeLeft, userExamTime?.milliseconds || 0, 'ms');
        return timeLeft;
    }, [examMaxTime, userExamTime]);

    useEffect(() => {
        try {
            const localTime = localStorage.getItem(userExamTimeKey);
            if (localTime) {
                const localTimeJSON = JSON.parse(localTime);
                setUserExamTime({...localTimeJSON, loaded: true});
            }
        } catch (e) {
            console.error(`Failed to load from local storage ${e}`);
        }

        getUserExamTime(uid, examKey).then(examTime => {
            setUserExamTime(current => ({
                hours: Math.max(examTime.hours, current.hours),
                minutes: Math.max(examTime.minutes, current.minutes),
                seconds: Math.max(examTime.seconds, current.seconds),
            }));
            setLoaded((l => ({...l, user: true})))
        })
    }, [examKey, uid, userExamTimeKey]);

    useEffect(() => {
        getExamTime(exam)
            .then(examTime => setExamMaxTime({...examTime, loaded: true}))
            .then(() => {
                setLoaded((l => ({...l, max: true})))
            });
    }, [exam]);


    const tick = useCallback((interval, unit, save) => {
        if (!userExamTime) {
            return;
        }
        if (paused) return;

        if (over) return;

        const examTime = incrementTime(userExamTime, interval, unit);
        localStorage.setItem(userExamTimeKey, JSON.stringify(examTime));

        setUserExamTime({
            hours: examTime.hours,
            minutes: examTime.minutes,
            seconds: examTime.seconds,
            milliseconds: examTime.milliseconds
        });
        if (save) {
            saveUserExamTime(uid, examKey, examTime)
        }
    }, [examKey, over, paused, uid, userExamTime, userExamTimeKey]);

    useEffect(() => {
        if (!over && !paused && loaded.max && loaded.user && time.hours === 0 && time.minutes === 0 && time.seconds === 0) {
            setOver(true);
            alert("The time of the exam is over. The exam will not end");
            saveUserExamTime(uid, examKey, {
                hours: examMaxTime.hours,
                minutes: examMaxTime.minutes,
                seconds: examMaxTime.seconds
            });
        }
    }, [examKey, examMaxTime, loaded.max, loaded.user, over, paused, time.hours, time.minutes, time.seconds, uid]);

    useEffect(() => {
        const interval = 100;

        if (paused) return;

        if (over) return;

        const intervalSubscription = setInterval(() => {
            saveTracker.current += interval;
            const save = saveTracker.current >= 30000;
            if (save) {
                saveTracker.current = 0;
            }
            tick(interval, 'ms', save);
        }, interval);

        return () => clearInterval(intervalSubscription);
    }, [over, paused, tick])

    // const reset = () => {
    //     setTime({
    //         hours: parseInt(hours),
    //         minutes: parseInt(minutes),
    //         seconds: parseInt(seconds)
    //     });
    //
    //     setBackgroundTime({
    //         hours: parseInt('96'),
    //         minutes: parseInt('00'),
    //         seconds: parseInt('00')
    //     });
    //     setPaused(false);
    //     setOver(false);
    // };

    if (!loaded.user || !loaded.max) {
        return <></>
    }

    return (
        <div style={{marginTop: '22px', marginLeft: '35%'}}>

            {
                background === 'true' ?
                    <p style={{fontSize: '0.5px', marginBottom: '10px'}}>Time
                        left <b>{`${time.hours.toString().padStart(2, '0')}:${time.minutes
                            .toString()
                            .padStart(2, '0')}:${time.seconds.toString().padStart(2, '0')}`}</b></p>
                    :
                    <p style={{fontSize: '15px', marginBottom: '10px'}}>Time
                        left <b>{`${time.hours.toString().padStart(2, '0')}:${time.minutes
                            .toString()
                            .padStart(2, '0')}:${time.seconds.toString().padStart(2, '0')}`}</b></p>
            }
            <div>{over ? '' : ''}</div>
            {/*{*/}
            {/*    background === 'true' ?*/}
            {/*        <></>*/}
            {/*        :*/}
            {/*        <img style={{width: '70px', cursor: 'pointer', marginRight: '5px', marginLeft: '-8px'}} src={require('../assets/images/reset_button.png')} onClick={() => reset()}/>*/}
            {/*}*/}
            {
                background !== 'true' ?
                    paused ?
                        <img alt={"start"} style={{width: '60px', cursor: 'pointer', marginLeft: '30px'}}
                             src={require('../assets/images/play_button.png')} onClick={() => setPaused(!paused)}/>
                        :
                        <img alt={"pause"} style={{width: '70px', cursor: 'pointer', marginLeft: '30px'}}
                             src={require('../assets/images/pause_button.png')} onClick={() => setPaused(!paused)}/>
                    :
                    <></>
            }

        </div>
    );
}
